import React, { useRef, useEffect, useCallback } from 'react';
import theme from '../theme';

const ITEM_HEIGHT = 40;
const VISIBLE_COUNT = 5;
const PADDING_COUNT = Math.floor(VISIBLE_COUNT / 2);

const hideScrollbarStyle = `
  .wheel-col-scroll::-webkit-scrollbar { display: none; }
`;

const hours = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const minutes = ['00', '15', '30', '45'];

function WheelColumn({ options, value, onChange, width = 56 }) {
  const containerRef = useRef(null);
  const scrollTimeout = useRef(null);

  const selectedIndex = options.indexOf(value);

  useEffect(() => {
    if (!containerRef.current) return;
    containerRef.current.scrollTop = selectedIndex * ITEM_HEIGHT;
  }, []);

  useEffect(() => {
    if (!containerRef.current) return;
    const target = selectedIndex * ITEM_HEIGHT;
    if (Math.abs(containerRef.current.scrollTop - target) > 2) {
      containerRef.current.scrollTop = target;
    }
  }, [selectedIndex]);

  const handleScroll = useCallback(() => {
    if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
    scrollTimeout.current = setTimeout(() => {
      const container = containerRef.current;
      if (!container) return;
      const index = Math.round(container.scrollTop / ITEM_HEIGHT);
      const clamped = Math.max(0, Math.min(options.length - 1, index));
      container.scrollTo({ top: clamped * ITEM_HEIGHT, behavior: 'smooth' });
      if (options[clamped] !== value) onChange(options[clamped]);
    }, 120);
  }, [options, value, onChange]);

  const handleItemClick = (index) => {
    const container = containerRef.current;
    if (!container) return;
    container.scrollTo({ top: index * ITEM_HEIGHT, behavior: 'smooth' });
  };

  return (
    <div style={{ position: 'relative', width, height: ITEM_HEIGHT * VISIBLE_COUNT }}>
      <div style={{
        position: 'absolute', top: ITEM_HEIGHT * PADDING_COUNT, left: 0, right: 0,
        height: ITEM_HEIGHT, background: theme.colors.primaryLight,
        borderRadius: theme.radius.sm, pointerEvents: 'none', zIndex: 0,
      }} />
      <div
        ref={containerRef}
        onScroll={handleScroll}
        className="wheel-col-scroll"
        style={{
          height: '100%', overflowY: 'scroll', scrollSnapType: 'y mandatory',
          WebkitOverflowScrolling: 'touch', position: 'relative', zIndex: 1,
          scrollbarWidth: 'none', msOverflowStyle: 'none',
        }}
      >
        <div style={{ height: ITEM_HEIGHT * PADDING_COUNT }} />
        {options.map((opt, i) => (
          <div
            key={opt}
            onClick={() => handleItemClick(i)}
            style={{
              height: ITEM_HEIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center',
              scrollSnapAlign: 'center', cursor: 'pointer',
              fontSize: opt === value ? theme.font.size.xl : theme.font.size.md,
              fontWeight: opt === value ? 700 : 400,
              color: opt === value ? theme.colors.primary : theme.colors.textMuted,
              transition: 'all 0.15s',
            }}>
            {opt}
          </div>
        ))}
        <div style={{ height: ITEM_HEIGHT * PADDING_COUNT }} />
      </div>
    </div>
  );
}

function TimeRangePicker({ fromTime, toTime, onChangeFrom, onChangeTo }) {
  const [fromH, fromM] = fromTime.split(':');
  const [toH, toM] = toTime.split(':');

  return (
    <div>
      <style>{hideScrollbarStyle}</style>
      <div style={{ display: 'flex', justifyContent: 'center', gap: 24 }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: theme.font.size.sm, color: theme.colors.textSecondary, marginBottom: 6, fontWeight: 600 }}>Dalle</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <WheelColumn options={hours} value={fromH} onChange={h => onChangeFrom(`${h}:${fromM}`)} />
            <span style={{ fontSize: theme.font.size.xl, fontWeight: 700, color: theme.colors.textPrimary }}>:</span>
            <WheelColumn options={minutes} value={fromM} onChange={m => onChangeFrom(`${fromH}:${m}`)} />
          </div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: theme.font.size.sm, color: theme.colors.textSecondary, marginBottom: 6, fontWeight: 600 }}>Alle</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <WheelColumn options={hours} value={toH} onChange={h => onChangeTo(`${h}:${toM}`)} />
            <span style={{ fontSize: theme.font.size.xl, fontWeight: 700, color: theme.colors.textPrimary }}>:</span>
            <WheelColumn options={minutes} value={toM} onChange={m => onChangeTo(`${toH}:${m}`)} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default TimeRangePicker;